import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

import JobListCard from '../../../../components/Card/JobListCard';
import useQueryString from '../../../../hooks/ui-control/useQueryString';
import { useAppSelector } from '../../../../redux/store';

interface JobsListProps {
  showMobileInfo?: boolean;
  setShowMobileInfo: (visible: boolean) => void;
  loading: boolean;
  guest?: boolean;
}

const JobsList: React.FC<JobsListProps> = ({
  showMobileInfo,
  setShowMobileInfo,
  loading,
  guest,
}) => {
  // Read and write URL query params
  const [queryParams, setQueryParams] = useQueryString();
  const currentJobId = queryParams.get('jobId');

  // Selectors
  const allJobs = useAppSelector((state) => state.locumJobs.jobData);

  // Track if initial URL had jobId
  const hadInitialJobId = useRef<boolean>(!!currentJobId);

  /**
   * On mount: determine if mobile info should be visible
   */
  useEffect(() => {
    setShowMobileInfo(hadInitialJobId.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Auto-pick first job when list loads and URL started empty,
   * but skip when in guest or invite mode
   */
  useEffect(() => {
    if (guest) return;

    if (!hadInitialJobId.current && allJobs.length > 0) {
      setQueryParams({ jobId: allJobs[0].id });
      hadInitialJobId.current = true;
    }
  }, [allJobs, guest, setQueryParams]);

  /**
   * Handle card click: update URL and toggle mobile info
   */
  const handleJobClick = (jobId: string) => {
    setQueryParams({ jobId });
    setShowMobileInfo(!showMobileInfo);
  };

  return (
    <Container className={showMobileInfo ? 'showMobileInfo' : ''}>
      {loading ? (
        <LoadingOutlined />
      ) : allJobs.length === 0 ? (
        <p>No jobs available or matching your search</p>
      ) : (
        allJobs.map((job) => (
          <JobListCard
            key={job.id}
            imgSrc="/static/img/company-placeholder.png"
            institutionName={job.institutionName}
            jobTitle={job.title}
            yearsOfExperience={job.yearsOfExperience}
            pay={job.pay}
            onClick={() => handleJobClick(job.id)}
            className={clsx(
              'cursor-pointer',
              job.id === currentJobId && 'active'
            )}
            jobId={job.id}
            jobDescription={job.jobDescription}
            guest={guest}
          />
        ))
      )}
    </Container>
  );
};

export default JobsList;

// ------ Styled Components ------

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 12px;

  @media (max-width: 884px) {
    display: grid;
    &.showMobileInfo {
      display: none;
    }
  }
`;
